import {ReactElement, useEffect, useState} from "react";
import {DocumentData, FirestoreError, Query, QuerySnapshot} from "firebase/firestore";
import {useCollection} from "react-firebase-hooks/firestore";

interface CollectionState<T> {
    scope: string;
    data?: QuerySnapshot<T>;
    loading: boolean;
    error?: FirestoreError;
}

interface SubscriptionProps<T> {
    source: Query<T> | null;
    scope: string;
    onChange: (state: CollectionState<T>) => void;
}

function CollectionSubscription<T>({source, scope, onChange}: SubscriptionProps<T>) {
    const [data, loading, error] = useCollection(source);
    useEffect(() => {
        onChange({scope, data, loading, error});
    }, [scope, data, loading, error, onChange]);
    return null;
}

interface ScopedCollection<T> {
    data?: QuerySnapshot<T>;
    loading: boolean;
    error?: FirestoreError;
    subscription: ReactElement;
}

/**
 * Един абонамент за конкретен отдел/период. Scope трябва да включва всички
 * променливи филтри на memoized заявката. При смяна React създава нов useCollection,
 * вместо да сменя вътрешния му ref едновременно с Redux обновяванията.
 * Subscription се рендерира до останалите loaders, без да ги размонтира.
 */
export function useScopedCollection<T = DocumentData>(source: Query<T> | null, scope: string): ScopedCollection<T> {
    const [state, setState] = useState<CollectionState<T>>({scope, loading: source !== null});
    const current = state.scope === scope;
    return {
        data: current ? state.data : undefined,
        loading: current ? state.loading : source !== null,
        error: current ? state.error : undefined,
        subscription: <CollectionSubscription key={scope} source={source} scope={scope} onChange={setState}/>,
    };
}
